import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Text,
  Badge,
  Button,
  HStack,
  Flex,
  Spinner,
  useToast,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Switch,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function AudioStudio() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null); // for conversion modal
  const [viewStory, setViewStory] = useState(null); // for view modal
  const [audioFile, setAudioFile] = useState(null);
  const [isPublished, setIsPublished] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // <-- SEARCH STATE

  const toast = useToast();
  const navigate = useNavigate();

  const RAW_BASE_URL =
    process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') ||
    'http://localhost:3030';
  const API_BASE = `${RAW_BASE_URL}/api`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchStories = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admin/user-stories`, {
        headers,
      });
      if (res.data?.success) setStories(res.data.data || []);
    } catch (error) {
      toast({ title: 'Fetch failed', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [API_BASE, token, toast]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const closeModal = () => {
    setSelectedStory(null);
    setAudioFile(null);
  };

  // 👇 YE NAYA FUNCTION ADD KARO
  const [viewLoadingId, setViewLoadingId] = useState(null);

  const handleViewStory = async (story) => {
    try {
      setViewLoadingId(story._id);
      const res = await axios.get(
        `${API_BASE}/admin/user-stories/${story._id}`,
        { headers },
      );
      if (res.data?.success) {
        setViewStory(res.data.data); // full data, audioFile ke saath
      } else {
        setViewStory(story); // fallback
      }
    } catch (error) {
      toast({
        title: 'Failed to load audio details',
        status: 'error',
        duration: 3000,
      });
      setViewStory(story); // fallback, modal to phir bhi khulega
    } finally {
      setViewLoadingId(null);
    }
  };

  const handleUpload = async () => {
    if (!audioFile || !selectedStory) return;

    const formData = new FormData();
    formData.append('audioTitle', 'Audio Version');
    formData.append('audioDescription', 'Converted to audio');
    formData.append('audioFile', audioFile);
    formData.append('isAudioPublished', isPublished ? 'true' : 'false');

    try {
      setUploading(true);
      const res = await axios.put(
        `${API_BASE}/admin/story/${selectedStory._id}/upload-audio`,
        formData,
        { headers },
      );

      if (res.data.success) {
        toast({
          title: res.data.message,
          status: 'success',
          duration: 3000,
        });
        setStories((prev) =>
          prev.map((s) => (s._id === selectedStory._id ? res.data.data : s)),
        );
        closeModal();
      }
    } catch (err) {
      toast({
        title: err.response?.data?.message || 'Upload failed',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  // ============ FILTERED STORIES BASED ON SEARCH ===========
  const filteredStories = stories.filter((story) => {
    const storyTitle = story.title?.toLowerCase() || '';
    const userName = story.user?.name?.toLowerCase() || '';
    const userEmail = story.user?.email?.toLowerCase() || '';
    const term = searchTerm.toLowerCase();
    return (
      storyTitle.includes(term) ||
      userName.includes(term) ||
      userEmail.includes(term)
    );
  });

  if (!token)
    return (
      <Alert status="error" mt="50px">
        <AlertIcon />
        Unauthorized. Login again.
      </Alert>
    );

  if (loading)
    return (
      <Flex justify="center" mt="100px">
        <Spinner size="xl" />
      </Flex>
    );

  const thStyle = {
    padding: '10px',
    textAlign: 'left',
    borderBottom: '2px solid #ddd',
  };
  const tdStyle = {
    padding: '12px',
    background: '#fff',
  };

  return (
    <Box mt={{ base: '110px', md: '130px' }} px={{ base: '20px', md: '40px' }}>
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
        <HStack spacing={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="outline"
            borderRadius="full"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Text fontSize="28px" fontWeight="800">
            Audio Studio 🎧
          </Text>
        </HStack>

        <Badge
          colorScheme="purple"
          fontSize="14px"
          px={4}
          py={2}
          borderRadius="full"
        >
          {filteredStories.length} Stories
        </Badge>
      </Flex>

      {/* ================= SEARCH INPUT ================= */}
      <Box mb={4}>
        <Input
          placeholder="Search by Story Title or User..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      <Box overflowX="auto">
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0 10px',
          }}
        >
          <thead>
            <tr
              style={{
                background: '#6B46C1',
                color: 'white',
              }}
            >
              <th style={thStyle}>User</th>
              <th style={thStyle}>Story</th>
              <th style={thStyle}>View</th>
            </tr>
          </thead>
          <tbody>
            {filteredStories.map((story) => (
              <tr key={story._id} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ ...tdStyle, color: 'gray', fontSize: '14px' }}>
                  {story.user?.name || story.user?.email || 'Unknown'}
                </td>
                <td style={tdStyle}>{story.title}</td>

                <td style={tdStyle}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => handleViewStory(story)}
                    isLoading={viewLoadingId === story._id}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
            {filteredStories.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: 'gray',
                  }}
                >
                  No stories found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      {/* ================= Modal ================= */}
      <Modal isOpen={!!selectedStory} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Convert "{selectedStory?.title}" to Audio</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Audio File</FormLabel>
              <Input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudioFile(e.target.files[0])}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="publish-switch" mb="0">
                Publish Audio
              </FormLabel>
              <Switch
                id="publish-switch"
                colorScheme="green"
                isChecked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeModal}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleUpload}
              isLoading={uploading}
              disabled={!audioFile}
            >
              Upload
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!viewStory} onClose={() => setViewStory(null)}>
        <ModalOverlay />
        <ModalContent borderRadius="2xl" p={2}>
          <ModalHeader>Story Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontWeight="700">Title: {viewStory?.title}</Text>
            <Text mt={2} color="gray.600">
              User:{' '}
              {viewStory?.user?.name || viewStory?.user?.email || 'Unknown'}
            </Text>
            <Text mt={2}>{viewStory?.description}</Text>
            <Text mt={2} color="gray.600">
              Category: {viewStory?.category?.name || 'N/A'}
            </Text>
            <Text mt={2} color="gray.600">
              Status: {viewStory?.status || 'N/A'}
            </Text>
            <Badge
              mt={3}
              colorScheme="purple"
              fontSize="14px"
              px={3}
              py={1}
              borderRadius="full"
            >
              📚 {viewStory?.chapters?.length || 0} Chapters
            </Badge>

            {viewStory?.audioFile && (
              <Box
                mt={4}
                px={4}
                py={3}
                borderRadius="xl"
                bg="green.50"
                border="1px solid"
                borderColor="green.200"
              >
                <Text fontWeight="700" color="green.600" mb={2}>
                  🎧 Story Audio Available
                </Text>
                <audio controls style={{ width: '100%' }}>
                  <source src={`${RAW_BASE_URL}${viewStory.audioFile}`} />
                </audio>
              </Box>
            )}

            <Box mt={4}>
              <Text fontWeight="600" mb={2}>
                Chapter Audio Tracks
              </Text>
              {!viewStory?.chapters?.length ? (
                <Text color="gray.500">No chapters available</Text>
              ) : (
                viewStory.chapters.map((chapter, index) => (
                  <Box
                    key={chapter._id || index}
                    p={4}
                    borderRadius="xl"
                    mb={3}
                    bg="white"
                    boxShadow="md"
                    borderLeft="5px solid #6B46C1"
                    transition="0.3s"
                    _hover={{ transform: 'scale(1.02)' }}
                  >
                    <Box>
                      <Text fontWeight="700" fontSize="16px">
                        {index + 1}. {chapter.title}
                      </Text>

                      <Text fontSize="13px" color="gray.600" mt={1}>
                        {chapter.description || 'No chapter description'}
                      </Text>

                      {/* Extra Details */}
                      <HStack mt={2} spacing={3} flexWrap="wrap">
                        {/* Status */}
                        <Badge
                          colorScheme={
                            chapter.isAudioPublished ? 'green' : 'yellow'
                          }
                          borderRadius="full"
                          px={2}
                        >
                          {chapter.isAudioPublished ? 'Published' : 'Draft'}
                        </Badge>

                        {/* Created Date */}
                        {chapter.createdAt && (
                          <Text fontSize="12px" color="gray.500">
                            📅{' '}
                            {new Date(chapter.createdAt).toLocaleDateString()}
                          </Text>
                        )}

                        {/* Duration */}
                        {chapter.duration && (
                          <Text fontSize="12px" color="gray.500">
                            ⏱ {chapter.duration} min
                          </Text>
                        )}
                      </HStack>
                    </Box>
                    {chapter.audioFile ? (
                      <audio controls style={{ width: '100%' }}>
                        <source src={`${RAW_BASE_URL}${chapter.audioFile}`} />
                      </audio>
                    ) : (
                      <Text color="gray.500">No audio for this chapter</Text>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setViewStory(null)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

